import { useState } from 'react';
import { KeyIcon } from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { ConfirmModal } from '../common/Modal';
import { CreateTokenModal } from './CreateTokenModal';
import {
  usePersonalApiTokens,
  useRevokePersonalApiToken,
} from '../../hooks/usePersonalApiTokens';
import type { PersonalApiToken } from '../../services/api/personalApiTokens';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/format';

function StatusBadge({ status }: { status: PersonalApiToken['status'] }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
        Active
      </span>
    );
  }
  const label = status === 'expired' ? 'Expired' : 'Revoked';
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
      {label}
    </span>
  );
}

/**
 * "Personal API tokens" profile section (D-08 — additive, mounted beside the
 * untouched legacy API Token card). Lists the caller's own tokens (including
 * revoked/expired rows, muted with a status badge, per D-10), mints new
 * tokens via CreateTokenModal, and revokes active tokens via a named
 * ConfirmModal (D-11 — no native browser confirm dialog, T-71-13 mitigation).
 */
export function PersonalApiTokensSection() {
  const { data: tokens, isLoading } = usePersonalApiTokens();
  const revokeToken = useRevokePersonalApiToken();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tokenToRevoke, setTokenToRevoke] = useState<PersonalApiToken | null>(null);

  const handleRevokeConfirm = () => {
    if (!tokenToRevoke) return;
    revokeToken.mutate(tokenToRevoke.id, {
      onSuccess: () => setTokenToRevoke(null),
    });
  };

  return (
    <div
      id="personal-api-tokens"
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <KeyIcon className="h-6 w-6 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Personal API tokens</h2>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>New token</Button>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Personal API tokens let external tools (e.g. bots) authenticate as you. Each token is
        shown in full only once, right after creation.
      </p>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : !tokens || tokens.length === 0 ? (
        <p className="text-sm text-gray-500">No personal API tokens yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Created</th>
                <th className="py-2 pr-4">Expires</th>
                <th className="py-2 pr-4">Last used</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4" />
              </tr>
            </thead>
            <tbody>
              {tokens.map((token) => {
                const isActive = token.status === 'active';
                return (
                  <tr
                    key={token.id}
                    className={`border-t border-gray-100 ${isActive ? '' : 'opacity-60'}`}
                  >
                    <td className="py-2 pr-4 font-medium text-gray-900">{token.name}</td>
                    <td className="py-2 pr-4 text-gray-600">
                      {formatDisplayDate(token.createdAt)}
                    </td>
                    <td className="py-2 pr-4 text-gray-600">
                      {formatDisplayDate(token.expiresAt)}
                    </td>
                    <td className="py-2 pr-4 text-gray-600">
                      {token.lastUsedAt ? formatDisplayDateTime(token.lastUsedAt) : 'Never'}
                    </td>
                    <td className="py-2 pr-4">
                      <StatusBadge status={token.status} />
                    </td>
                    <td className="py-2 pr-4 text-right">
                      {isActive && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => setTokenToRevoke(token)}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateTokenModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />

      <ConfirmModal
        isOpen={tokenToRevoke !== null}
        onClose={() => setTokenToRevoke(null)}
        onConfirm={handleRevokeConfirm}
        title="Revoke token"
        message={`Revoke '${tokenToRevoke?.name ?? ''}'? Requests using it will fail immediately.`}
        confirmText="Revoke"
        variant="danger"
        isLoading={revokeToken.isPending}
        error={revokeToken.isError ? 'Failed to revoke token' : null}
      />
    </div>
  );
}
